"use client";

import { PostCard } from "@/components/blog/post-card";
import { ErrorBox } from "@/components/ui/ErrorBox";
import { LoadingBox } from "@/components/ui/LoadingBox";
import { BentoGrid } from "@/components/ui/grids/BentoGrid";
import { getUserBookmarks } from "@/lib/blog/bookmarks";
import { getPost } from "@/lib/blog/static";
import type {
	BlogPostBookmark,
	EnrichedBlogPost,
	EnrichedProfile,
} from "@/lib/supabase/types";
import { HeartCrackIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

interface ProfileBookmarksProps {
	profile: EnrichedProfile | null;
}

export default function ProfileBookmarks({ profile }: ProfileBookmarksProps) {
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const [posts, setPosts] = useState<EnrichedBlogPost[]>([]);
	const [bookmarks, setBookmarks] = useState<BlogPostBookmark[]>([]);

	const fetchPosts = useCallback(async () => {
		setLoading(true);
		if (!profile?.id) {
			console.log("User ID not found");
			setLoading(false);
			return;
		}

		try {
			const { data } = await getUserBookmarks(profile.id);
			setBookmarks(data ?? []);

			const postsData: EnrichedBlogPost[] = [];
			if (data) {
				for (const bookmark of data) {
					const { post, error } = await getPost(bookmark.post_id);
					if (error) {
						console.error(error);
						continue;
					}
					postsData.push(post as EnrichedBlogPost);
				}
			}
			setPosts(postsData);
		} catch (error) {
			console.error("Error fetching bookmarks:", error);
			setError("Failed to load bookmarks");
		} finally {
			setLoading(false);
		}
	}, [profile?.id]);

	useEffect(() => {
		fetchPosts();
	}, [fetchPosts]);

	if (loading) {
		return <LoadingBox message="Loading profile bookmarks" />;
	}

	if (error) {
		return <ErrorBox error="Error loading profile bookmarks" />;
	}

	return (
		<div className="w-full flex flex-col gap-2 mb-10">
			{/* Header */}
			<div className="w-full flex flex-row justify-between gap-4 sm:items-center">
				<h1 className="text-primary text-xl font-semibold md:text-2xl">
					Bookmarks
				</h1>
			</div>

			{/* Posts */}
			<div className="space-y-1 md:space-y-2">
				{bookmarks.length < 1 ? (
					<div className="w-full items-center flex flex-col gap-2 justify-center my-4">
						<HeartCrackIcon className="size-12 text-muted-foreground" />
						<p>No bookmarks found</p>
					</div>
				) : (
					<BentoGrid className="grow relative h-full">
						{posts.map((post, index) => {
							return <PostCard key={post.id} post={post} />;
						})}
					</BentoGrid>
				)}
			</div>
		</div>
	);
}
